/**
 * The soil-depth ruler beside the image's left edge, in a gutter of its own so
 * it never covers the image. It is told where the image's top and bottom are
 * on screen, so it follows zoom and pan, and only spans the part of the image
 * that is in view.
 */
import { type DepthSetup, depthCmAt } from '@/lib/depth';

/** The gutter the viewer keeps for the scale, left of the image. */
export const DEPTH_GUTTER_PX = 34;

// Candidate spacings between labelled ticks, in cm. The precision of the
// setup is a few cm, so nothing finer than 1 cm.
const MAJOR_STEPS_CM = [1, 2, 5, 10, 20, 50];
// Labelled ticks are at least this far apart on screen.
const MIN_MAJOR_PX = 40;
// Unlabelled ticks in between, only when they keep at least this gap.
const MIN_MINOR_PX = 7;

interface DepthScaleProps {
    setup: DepthSetup;
    /** Where the image's top and bottom are, in px down the viewer. */
    imageTopPx: number;
    imageBottomPx: number;
    /** The image's real height, from the top of the glass, in cm. */
    imageHeightCm: number;
    /** The viewer's height in px: ticks outside it are not drawn. */
    viewHeightPx: number;
}

export default function DepthScale({
    setup,
    imageTopPx,
    imageBottomPx,
    imageHeightCm,
    viewHeightPx,
}: DepthScaleProps) {
    const spanPx = imageBottomPx - imageTopPx;
    if (spanPx <= 0 || imageHeightCm <= 0) return null;

    const depthTop = depthCmAt(setup, 0);
    const depthBottom = depthCmAt(setup, imageHeightCm);
    const pxPerCm = spanPx / (depthBottom - depthTop);
    const yOf = (depth: number) =>
        imageTopPx + (depth - depthTop) * pxPerCm;

    const major =
        MAJOR_STEPS_CM.find((s) => s * pxPerCm >= MIN_MAJOR_PX) ??
        MAJOR_STEPS_CM[MAJOR_STEPS_CM.length - 1];
    // 5 or 2 subdivisions, whichever fits and stays on whole cm.
    const minor = [major / 5, major / 2].find(
        (s) => Number.isInteger(s) && s * pxPerCm >= MIN_MINOR_PX,
    );

    // The strip covers the visible part of the image only.
    const stripTop = Math.max(imageTopPx, 0);
    const stripBottom = Math.min(imageBottomPx, viewHeightPx);
    if (stripBottom <= stripTop) return null;

    // Depths of the ticks in view.
    const fromDepth = depthTop + (stripTop - imageTopPx) / pxPerCm;
    const toDepth = depthTop + (stripBottom - imageTopPx) / pxPerCm;
    const step = minor ?? major;
    const ticks: { depth: number; major: boolean }[] = [];
    for (
        let d = Math.ceil(fromDepth / step) * step;
        d <= toDepth;
        d += step
    ) {
        ticks.push({ depth: d, major: d % major === 0 });
    }
    const firstMajor = ticks.find((t) => t.major)?.depth;

    return (
        <div
            className="absolute inset-x-0 border-r border-[#cdd0c7]"
            style={{ top: stripTop, height: stripBottom - stripTop }}
            aria-hidden
        >
            {ticks.map(({ depth, major: isMajor }) => {
                const y = yOf(depth) - stripTop;
                return (
                    <div
                        key={depth}
                        className="absolute right-0 flex items-center"
                        style={{ top: y, transform: 'translateY(-50%)' }}
                    >
                        {isMajor && (
                            <span className="relative mr-1 text-[10px] font-medium tabular-nums leading-none text-[#5a6259]">
                                {depth}
                                {/* The unit once, under the top label rather
                                    than beside it, so the gutter is only as
                                    wide as the numbers; out of the flow, so
                                    the number stays level with its tick. */}
                                {depth === firstMajor && (
                                    <span className="absolute right-0 top-full mt-0.5 font-normal text-[#7a8077]">
                                        cm
                                    </span>
                                )}
                            </span>
                        )}
                        <span
                            className={
                                isMajor
                                    ? 'h-px w-2.5 bg-[#5a6259]'
                                    : 'h-px w-1.5 bg-[#a8aea3]'
                            }
                        />
                    </div>
                );
            })}
        </div>
    );
}
